import {
  leaderboardClient,
  UpdateTags2Request,
} from '@neptune/shared/core-apis-leaderboard-domain';
import { listEntities } from '@neptune/shared/entity-domain';

import { AsyncActionsReturnType, createAsyncActions } from 'state/async-actions';
import { BulkActionWithOriginRequest } from 'state/project-leaderboard/bulk-operations/actions';

type ManageTagsTarget = {
  id: string;
  projectIdentifier: string;
};

export function openManageTags(targets: ManageTagsTarget[], attributeName: string | string[]) {
  const attributeNames = Array.isArray(attributeName) ? attributeName : [attributeName];
  const projectIdentifier = getSingleProjectIdentifier(targets);

  return {
    type: 'TAGS_MANAGE_OPEN',
    payload: {
      projectIdentifier,
      shortIds: targets.map(({ id }) => id),
      attributeNames,
    },
  } as const;
}

function getSingleProjectIdentifier(targets: ManageTagsTarget[]): string | undefined {
  const projectIdentifiers = Array.from(
    new Set(targets.map(({ projectIdentifier }) => projectIdentifier)),
  );

  return projectIdentifiers.length === 1 ? projectIdentifiers[0] : undefined;
}

export function closeManageTags() {
  return { type: 'TAGS_MANAGE_CLOSE' } as const;
}

export enum FetchEntriesForManageTagsActionTypes {
  request = 'MANAGE_TAGS_FETCH_ENTRIES_REQUEST',
  success = 'MANAGE_TAGS_FETCH_ENTRIES_SUCCESS',
  fail = 'MANAGE_TAGS_FETCH_ENTRIES_FAIL',
}

export type FetchEntriesForManageTagsParams = {
  projectIdentifier: string;
  shortIds: string[];
  attributeNames: string[];
};

const fetchEntriesActions = createAsyncActions({
  types: FetchEntriesForManageTagsActionTypes,
  resolver: async ({
    projectIdentifier,
    shortIds,
    attributeNames,
  }: FetchEntriesForManageTagsParams) => {
    const response = await listEntities(
      {
        projectIdentifier,
        runsIds: shortIds,
        attributePathsFilter: attributeNames,
      },
      {
        filterArchived: false,
        flattenEntities: true,
      },
    );

    return {
      entries: response,
      request: {
        projectIdentifier,
        shortIds,
        attributeNames,
      },
    };
  },
});

export enum ApplyTagsActionTypes {
  request = 'MANAGE_TAGS_APPLY_TAGS_REQUEST',
  success = 'MANAGE_TAGS_APPLY_TAGS_SUCCESS',
  fail = 'MANAGE_TAGS_APPLY_TAGS_FAIL',
}

const applyTagsActions = createAsyncActions({
  types: ApplyTagsActionTypes,
  resolver: async (params: BulkActionWithOriginRequest<UpdateTags2Request>) => {
    const { origin, ...requestParams } = params;
    const response = await leaderboardClient.updateTags2(requestParams);
    return {
      origin: params.origin,
      response,
    };
  },
});

export const { execute: fetchEntries } = fetchEntriesActions;
export const { execute: applyTags } = applyTagsActions;

export type ApplyTagsActions = AsyncActionsReturnType<typeof applyTagsActions>;

export type ManageTagsActions =
  | ApplyTagsActions
  | AsyncActionsReturnType<typeof fetchEntriesActions>
  | ReturnType<typeof openManageTags>
  | ReturnType<typeof closeManageTags>;
